/**
 * Logic thuần của màn "Nhập danh sách khách" (STT 2.2, ADR-089): đọc chữ dán thành các dòng gửi máy chủ, dựng
 * bản xem trước (dòng cần xem lên đầu), khoá chống lặp theo nội dung, câu lỗi, trạng thái nút.
 *
 * Dùng CHUNG hàm đọc bảng của máy chủ (`@sapa/shared/nhap-khach`) để xem trước tức thì; kết luận từng dòng
 * (mới / đã có / lỗi + mã) LUÔN lấy từ máy chủ. Không bao giờ ghi danh sách vào log / console / localStorage.
 */
import {
  type CotNhap,
  chuanHoaDongNhap,
  chuanHoaSdtVnChiTiet,
  type DongTho,
  doCot,
  hienSdtVn,
  layDongTho,
  type MaDongNhap,
  NHAP_KHACH_TOI_DA_DONG,
  tachBang,
} from '@sapa/shared'
import type { KetQuaNhapKhach } from './api.ts'
import { KH } from './text/khach.ts'

/** Số dòng vẽ mỗi lần ở mỗi danh sách — 2.000 dòng không được vẽ một lượt (cuộn mượt trên máy yếu). */
export const TRANG_HIEN = 200

export type LoiDocBang = 'TRONG' | 'KHONG_THAY_COT_TEN' | 'QUA_DONG'

export interface BangDoc {
  /** Các dòng ô thô, đúng thứ tự — gửi nguyên cho máy chủ. */
  dong: DongTho[]
  /** Số dòng gốc (1-based) của từng dòng — khớp số dòng Excel nếu chủ tiệm chép từ ô A1. */
  soDongGoc: number[]
  /** Các cột đã hiểu, theo thứ tự cố định. */
  cot: CotNhap[]
  coTieuDe: boolean
}

const THU_TU_COT: readonly CotNhap[] = ['ten', 'sdt', 'ngaySinh', 'luuY', 'lanCuoi']

export function docBang(van: string): { loi: LoiDocBang } | { loi: null; bang: BangDoc } {
  const t = tachBang(van)
  if (t.o.length === 0) return { loi: 'TRONG' }
  const c = doCot(t.o)
  if (c.loi !== null) return { loi: 'KHONG_THAY_COT_TEN' }
  const tu = c.dongTieuDe === null ? 0 : c.dongTieuDe + 1
  const dong = layDongTho(t.o, c.cot, tu)
  if (dong.length === 0) return { loi: 'TRONG' }
  if (dong.length > NHAP_KHACH_TOI_DA_DONG) return { loi: 'QUA_DONG' }
  return {
    loi: null,
    bang: {
      dong,
      soDongGoc: dong.map((_, i) => t.dongGoc[tu + i] ?? tu + i + 1),
      cot: THU_TU_COT.filter((k) => c.cot[k] !== undefined),
      coTieuDe: c.dongTieuDe !== null,
    },
  }
}

/** "Tên · Số điện thoại · Ngày sinh" — các cột máy đã hiểu. */
export function cauCotDaHieu(cot: readonly CotNhap[]): string {
  return THU_TU_COT.filter((k) => cot.includes(k))
    .map((k) => KH.tenCot[k])
    .join(' · ')
}

/**
 * Câu nói máy đã hiểu cột nào. Không có dòng tiêu đề thì máy ĐOÁN — và chỉ được nói "đoán cột Số điện thoại" khi
 * thật sự có cột số trong bản đoán (ui-critic vòng 36: hai hộp nói ngược nhau).
 */
export function cauVeCot(bang: BangDoc): string {
  if (bang.coTieuDe) return KH.cotDaHieu(cauCotDaHieu(bang.cot))
  return bang.cot.includes('sdt') ? KH.khongTieuDe : KH.khongTieuDeChiTen
}

/** Cảnh báo khi KHÔNG dòng nào đọc ra số: chưa thấy cột số, hay có cột số mà không đọc được số nào. */
export function cauThieuSo(bang: BangDoc): string {
  return bang.cot.includes('sdt') ? KH.khongDocDuocSo : KH.khongCoCotSdt
}

export interface DongHien {
  /** Chỉ số trong mảng đã gửi (0-based). */
  chiSo: number
  /** Ô thô của dòng — để chép lại danh sách dòng chưa nhập. */
  tho: DongTho
  /** Dòng này có đọc ra được số điện thoại để gắn vào hồ sơ không. */
  coSo: boolean
  /** Số dòng gốc để người dùng đối chiếu với sổ. */
  soDong: number
  /** Tên đã làm sạch; không có thì chữ "Không có tên". */
  ten: string
  /** Số đã chuẩn hoá dạng đọc to, hoặc ô thô khi không đọc được; '' nếu ô trống. */
  sdt: string
  /**
   * `loi` sẽ không nhập · `chu-y` máy đã sửa giúp · `khong-so` chỉ thiếu số (vẫn nhập, máy không sửa gì) ·
   * `on` ổn / đã có hồ sơ.
   */
  nhom: 'loi' | 'chu-y' | 'khong-so' | 'on'
  kq: 'moi' | 'da-co' | 'loi'
  lyDo: string[]
}

export interface BanXemTruoc {
  /** Lỗi (không nhập) rồi tới dòng đã sửa giúp — mỗi nhóm giữ thứ tự dòng. */
  canXem: DongHien[]
  /** Dòng ổn + dòng đã có hồ sơ. */
  khac: DongHien[]
  soMoi: number
  soDaCo: number
  /** Dòng SẼ KHÔNG nhập (thiếu tên, trùng số trong tệp, trùng tên không số…). */
  soKhongNhap: number
  /** Dòng SẼ nhập mà máy THẬT SỰ đã sửa giúp (đổi đầu số, chép số / ngày vào lưu ý, cắt…) — nên xem lại. */
  soDaSua: number
  /** Dòng SẼ nhập nhưng không có số điện thoại (máy không sửa gì) — kể cả dòng cũng có chú ý khác. */
  soKhongSo: number
  /** = soKhongNhap + soDaSua. */
  soCanXem: number
  /** Các dòng KHÔNG nhập (nhóm `loi`), theo thứ tự dòng. */
  chuaNhap: DongHien[]
  /** Các dòng máy đã sửa giúp (nhóm `chu-y`), theo thứ tự dòng. */
  daSua: DongHien[]
  /** Các dòng chỉ thiếu số (nhóm `khong-so`), theo thứ tự dòng. */
  khongSo: DongHien[]
  /** Không dòng nào đọc ra được số điện thoại — nhiều khả năng dán nhầm bảng (dịch vụ, giá…). */
  khongCoSoNao: boolean
}

/** "01689…" khi ô mất số 0 ở đầu: lấy dãy số đầu tiên trong ô thô, thêm "0" nếu thiếu. */
function hieuLaSoCu(sdtTho: string | null): string {
  const m = /\+?\d[\d\s.\-()]{7,}\d/.exec(sdtTho ?? '')
  const so = (m?.[0] ?? sdtTho ?? '').replace(/\D/g, '')
  return so.startsWith('0') ? so : `0${so}`
}

/** Số cũ NGUYÊN VĂN như trong sổ: dãy số đầu tiên trong ô thô (giữ dấu cách / chấm / gạch của người nhập). */
function soCuNguyenVan(sdtTho: string | null): string {
  const m = /\+?\d[\d\s.\-()]{7,}\d/.exec(sdtTho ?? '')
  return (m?.[0] ?? sdtTho ?? '').trim()
}

interface NguCanhHien {
  ctx: { homNay: string; kieuNgaySinh: 'dmy' | 'mdy'; kieuLanCuoi: 'dmy' | 'mdy' }
  soDongGoc: readonly number[]
}

/**
 * Nhóm của một dòng: chỉ mã `KHONG_CO_SO` thì máy KHÔNG sửa gì (chỉ báo thiếu số) → `khong-so`; còn mã nào khác
 * thì máy đã đổi / chép / cắt → `chu-y`.
 */
function nhomCua(kq: 'moi' | 'da-co' | 'loi', ma: readonly MaDongNhap[]): DongHien['nhom'] {
  if (kq === 'loi') return 'loi'
  if (kq !== 'moi' || ma.length === 0) return 'on'
  return ma.every((m) => m === 'KHONG_CO_SO') ? 'khong-so' : 'chu-y'
}

/** Một dòng thô + kết luận của máy chủ → dòng hiện trên màn xem trước. */
function dongHienCua(
  tho: DongTho,
  r: KetQuaNhapKhach['dong'][number],
  i: number,
  c: NguCanhHien,
): DongHien {
  const sach = chuanHoaDongNhap(tho, c.ctx)
  const soDienDoc = sach.sdt === null ? '' : hienSdtVn(sach.sdt)
  const hieuLa = hieuLaSoCu(tho.sdt)
  const soDoan = chuanHoaSdtVnChiTiet(hieuLa, { nhanThieuSo0Cu: true }).so
  const ngucCanh = {
    // Dấu cách không ngắt: số mới không bị bẻ đôi giữa câu ở màn hẹp.
    soMoi: soDienDoc.replace(/ /g, '\u00a0'),
    soCu: soCuNguyenVan(tho.sdt).replace(/\s+/g, '\u00a0'),
    hieuLa,
    soDoan: soDoan === null ? '' : hienSdtVn(soDoan).replace(/ /g, '\u00a0'),
    ...(r.trungVoi === undefined ? {} : { dong: c.soDongGoc[r.trungVoi] ?? r.trungVoi + 1 }),
  }
  // Dòng không nhập (lỗi / đã có): chỉ nói lý do chính — chú ý của dòng không được ghi thì vô nghĩa.
  const ma: MaDongNhap[] = r.kq === 'moi' ? r.ma : r.ma.slice(0, 1)
  const nhom: DongHien['nhom'] = nhomCua(r.kq, r.ma)
  return {
    chiSo: i,
    tho,
    coSo: sach.sdt !== null,
    soDong: c.soDongGoc[i] ?? i + 1,
    ten: sach.ten ?? tho.ten ?? KH.khongCoTen,
    sdt: soDienDoc !== '' ? soDienDoc : (tho.sdt ?? ''),
    nhom,
    kq: r.kq,
    lyDo: ma.map((m) => KH.lyDo(m, ngucCanh)),
  }
}

export function dungXemTruoc(bang: BangDoc, kq: KetQuaNhapKhach, homNay: string): BanXemTruoc {
  const c: NguCanhHien = {
    ctx: { homNay, kieuNgaySinh: kq.kieuNgay.ngaySinh, kieuLanCuoi: kq.kieuNgay.lanCuoi },
    soDongGoc: bang.soDongGoc,
  }
  const loi: DongHien[] = []
  const chuY: DongHien[] = []
  const khongSo: DongHien[] = []
  const khac: DongHien[] = []
  let soMoi = 0
  let soDaCo = 0
  let soKhongSo = 0
  let soDong = 0
  let soCoSo = 0
  bang.dong.forEach((tho, i) => {
    const r = kq.dong[i]
    if (r === undefined) return
    const d = dongHienCua(tho, r, i, c)
    if (r.kq === 'moi') soMoi++
    if (r.kq === 'da-co') soDaCo++
    if (r.kq === 'moi' && r.ma.includes('KHONG_CO_SO')) soKhongSo++
    soDong++
    if (d.coSo) soCoSo++
    const nhomDs =
      d.nhom === 'loi' ? loi : d.nhom === 'chu-y' ? chuY : d.nhom === 'khong-so' ? khongSo : khac
    nhomDs.push(d)
  })
  const canXem = [...loi, ...chuY]
  return {
    canXem,
    khac,
    soMoi,
    soDaCo,
    soKhongNhap: loi.length,
    soDaSua: chuY.length,
    soKhongSo,
    soCanXem: canXem.length,
    chuaNhap: loi,
    daSua: chuY,
    khongSo,
    khongCoSoNao: soDong > 0 && soCoSo === 0,
  }
}

export interface PhanDem {
  chu: string
  /** `khong-nhap` đỏ đậm · `dam` câu nói điều chính (0 khách mới) · `thuong`. */
  kieu: 'thuong' | 'khong-nhap' | 'dam'
}

/**
 * Các dòng của thẻ đếm xem trước, phần bằng 0 không hiện. 0 khách mới → câu "Không có khách mới nào để nhập."
 * thay cho dòng "0 khách mới" (vòng 36).
 */
export function phanTheDem(ban: BanXemTruoc): PhanDem[] {
  const ra: PhanDem[] = []
  if (ban.soMoi > 0) ra.push({ chu: KH.demMoi(ban.soMoi), kieu: 'thuong' })
  else ra.push({ chu: KH.khongCoKhachMoi, kieu: 'dam' })
  if (ban.soDaCo > 0) ra.push({ chu: KH.demDaCo(ban.soDaCo), kieu: 'thuong' })
  if (ban.soKhongNhap > 0) ra.push({ chu: KH.demKhongNhap(ban.soKhongNhap), kieu: 'khong-nhap' })
  if (ban.soDaSua > 0) ra.push({ chu: KH.demDaSua(ban.soDaSua), kieu: 'thuong' })
  if (ban.soKhongSo > 0) ra.push({ chu: KH.demKhongSo(ban.soKhongSo), kieu: 'thuong' })
  return ra
}

/**
 * Chân tờ khi KHÔNG có khách mới để nhập: vẫn phải có một nút chính. Còn dòng không nhập → "Dán lại" (sửa rồi
 * thử lại); không còn gì để sửa (toàn khách đã có hồ sơ) → "Xong".
 */
export function nutChinhKhiKhongMoi(soKhongNhap: number): 'dan-lai' | 'xong' {
  return soKhongNhap > 0 ? 'dan-lai' : 'xong'
}

/** Kết quả sau khi nhập: đã nhập + đã có + chưa nhập = tổng số dòng đã gửi (máy chủ trả mỗi dòng đúng một kết luận). */
export function tomTatKetQua(kq: KetQuaNhapKhach): {
  moi: number
  daCo: number
  chua: number
  tong: number
} {
  return { moi: kq.soMoi, daCo: kq.soDaCo, chua: kq.soLoi, tong: kq.dong.length }
}

/** Một ô để dán vào Excel: không tab / xuống dòng; ô mở bằng = + - @ được thêm `'` để không bị coi là công thức. */
function oChoExcel(x: string): string {
  const g = x.replace(/[\t\r\n\u00a0]+/g, ' ').trim()
  return /^[=+\-@]/.test(g) ? `'${g}` : g
}

/**
 * Văn bản các dòng chưa nhập, cách nhau bằng tab, có dòng tiêu đề (cùng tên cột máy nhận ra khi dán lại) + cột
 * "Lý do chưa nhập". Chỉ chép vào clipboard theo yêu cầu của chủ tiệm — không ghi nơi nào khác.
 */
export function vanBanChuaNhap(bang: BangDoc, ds: readonly DongHien[]): string {
  const dong: string[] = [[...bang.cot.map((k) => KH.tenCot[k]), KH.cotChuaNhap].join('\t')]
  for (const d of ds) {
    dong.push(
      [...bang.cot.map((k) => oChoExcel(d.tho[k] ?? '')), oChoExcel(d.lyDo.join('; '))].join('\t'),
    )
  }
  return dong.join('\n')
}

/** Khoá chống lặp theo NỘI DUNG đã dán: bấm lại sau lỗi mạng = cùng khoá; đổi chữ dán = khoá mới. */
export function khoaChoNoiDung(
  cu: { noiDung: string; khoa: string } | null,
  noiDung: string,
  taoKhoa: () => string,
): { noiDung: string; khoa: string } {
  if (cu !== null && cu.noiDung === noiDung) return cu
  return { noiDung, khoa: taoKhoa() }
}

/**
 * Câu lỗi + có giữ khoá không. Mất mạng / 5xx / 429: chưa chắc đã nhập xong → giữ khoá, bấm lại không nhập đôi.
 * Lỗi còn lại (400 / 403 / 413): câu của máy chủ (đã là tiếng Việt, không vọng lại dữ liệu).
 */
export function cauLoiNhap(e: { status: number; code: string; messageVi: string }): {
  cau: string
  giuKhoa: boolean
} {
  if (e.status === 0 || e.status >= 500) return { cau: KH.nhapMangCham, giuKhoa: true }
  const cau = e.messageVi.trim() === '' ? KH.nhapLoiChung : e.messageVi
  return { cau, giuKhoa: e.status === 429 }
}

export function nutXemTruoc(chu: string, dang: boolean): { tat: boolean } {
  return { tat: dang || chu.trim() === '' }
}

export function nutNhap(
  soMoi: number,
  dang: boolean,
): { hien: false } | { hien: true; tat: boolean; nhan: string } {
  if (soMoi <= 0) return { hien: false }
  return { hien: true, tat: dang, nhan: dang ? KH.dangNhap : KH.nhapKhach(soMoi) }
}
